/* eslint-disable jsx-a11y/no-static-element-interactions */
/* eslint-disable jsx-a11y/click-events-have-key-events */
import {
  ArrowRightCircleIcon,
  DocumentTextIcon,
  PhoneIcon,
  SpeakerWaveIcon,
} from '@heroicons/react/24/outline';
import React, { useState } from 'react';

import { EnhancedCallResultSchema } from '@/types/enhanced-bland-call';

import { formatDate } from '../voice-agent/utils';
import { DevCallRouter } from './DevCallRouter';
import { StatusIndicator } from './StatusIndicator';

interface CallRowProps {
  call: EnhancedCallResultSchema;
  runStatus?: string;
  workflowId?: string;
  showRunIndicator?: boolean;
  isSelected?: boolean;
  onSelect?: (callId: string) => void;
  onPlayRecording?: (callId: string) => void;
  onViewTranscript?: (callId: string) => void;
  onRouted?: () => void;
  className?: string;
}

export const CallRow: React.FC<CallRowProps> = ({
  call,
  runStatus,
  workflowId,
  showRunIndicator = false,
  isSelected = false,
  onSelect,
  onPlayRecording,
  onViewTranscript,
  onRouted,
  className = '',
}) => {
  const [showRouter, setShowRouter] = useState(false);

  const hasTranscript = call.transcript_available && call.transcript;
  const hasRecording = false; // TODO: Add recording availability check
  const isInProgress =
    call.status === 'in_progress' || call.status === 'call_in_progress';
  // Check if the call itself is dev_stalled (not the run)
  const isDevStalled = call.status === 'dev_stalled';

  return (
    <div
      className={`
        flex items-center py-1 px-2 text-xs hover:bg-gray-50 border-l-2 
        ${isSelected ? 'bg-blue-50 border-blue-400' : 'border-gray-200'}
        ${isInProgress ? 'animate-pulse' : ''}
        ${className}
      `}
      onClick={() => onSelect?.(call.id)}
    >
      {/* Selection checkbox */}
      {onSelect && (
        <input
          type="checkbox"
          checked={isSelected}
          onChange={() => onSelect(call.id)}
          className="mr-2 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
          onClick={(e) => e.stopPropagation()}
        />
      )}

      {/* Call icon with status */}
      <div className="w-4 h-4 mr-2 relative">
        <PhoneIcon
          className={`h-4 w-4 ${isInProgress ? 'text-green-500' : 'text-gray-400'}`}
        />
        {isInProgress && (
          <div className="absolute -top-1 -right-1 w-2 h-2 bg-green-500 rounded-full animate-ping" />
        )}
      </div>

      <div className="flex-1 flex items-center gap-3 min-w-0">
        {/* Phone number */}
        <span className="font-mono text-gray-900 truncate w-24">
          {call.phone_number}
        </span>

        {/* Call status */}
        <div className="w-14">
          <StatusIndicator
            status={call.status || 'unknown'}
            size="sm"
            className={isInProgress ? 'animate-pulse' : ''}
          />
        </div>

        {/* Run status indicator */}
        {showRunIndicator && runStatus && (
          <div className="w-14">
            <StatusIndicator
              status={runStatus}
              isScheduled={runStatus === 'scheduled'}
              size="sm"
            />
          </div>
        )}

        {/* Duration */}
        <span className="font-mono text-gray-600 w-12 text-right">
          {call.duration_minutes
            ? `${call.duration_minutes.toFixed(1)}m`
            : '--'}
        </span>

        {/* Cost */}
        <span className="font-mono text-gray-600 w-16 text-right">
          {call.cost_usd ? `$${call.cost_usd.toFixed(2)}` : '--'}
        </span>

        {/* Answered by */}
        <span className="text-gray-500 truncate flex-1">
          {call.answered_by ||
            (call.status === 'completed' ? 'No answer' : '—')}
        </span>

        {/* Timestamp */}
        <span className="text-gray-400 w-16 text-right">
          {call.created_at
            ? formatDate(call.created_at, { fromNow: true })
            : '—'}
        </span>

        {/* Action buttons */}
        <div className="flex items-center gap-1 ml-2">
          {hasRecording && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onPlayRecording?.(call.id);
              }}
              className="text-blue-600 hover:text-blue-800 p-1 rounded"
              title="Play recording"
            >
              <SpeakerWaveIcon className="h-3 w-3" />
            </button>
          )}

          {hasTranscript && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onViewTranscript?.(call.id);
              }}
              className="text-green-600 hover:text-green-800 p-1 rounded"
              title="View transcript"
            >
              <DocumentTextIcon className="h-3 w-3" />
            </button>
          )}

          {/* Dev routing button for stalled calls */}
          {isDevStalled && workflowId && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                setShowRouter(true);
              }}
              className="text-amber-600 hover:text-amber-800 p-1 rounded animate-pulse"
              title="Route test call"
            >
              <ArrowRightCircleIcon className="h-3 w-3" />
            </button>
          )}
        </div>
      </div>

      {/* Error indicator */}
      {call.error_message && (
        <div
          className="w-2 h-2 bg-red-500 rounded-full ml-1"
          title={call.error_message}
        />
      )}

      {/* Dev call router dialog */}
      {isDevStalled && workflowId && (
        <DevCallRouter
          workflowId={call.workflow_id || ''}
          phoneNumber={call.phone_number || ''}
          open={showRouter}
          onOpenChange={setShowRouter}
          onRouted={onRouted}
        />
      )}
    </div>
  );
};
